import { useEffect, useMemo } from "react";
import { AlertCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  InitialClientContractDraft,
  normalizeInitialContractServiceIds,
  toggleInitialContractService,
} from "@/lib/clientContractDraft";

interface ClientInitialContractTabProps {
  channelId?: number | null;
  value: InitialClientContractDraft;
  onChange: (next: InitialClientContractDraft) => void;
  fieldErrors?: Record<string, string>;
  readOnly?: boolean;
}

export default function ClientInitialContractTab({
  channelId,
  value,
  onChange,
  fieldErrors = {},
  readOnly,
}: ClientInitialContractTabProps) {
  const summaryInput = useMemo(() => {
    if (!channelId || channelId <= 0) return undefined;
    return { channelId };
  }, [channelId]);

  const { data: summary, isLoading } = trpc.platformContracts.summary.useQuery(summaryInput ?? {}, {
    enabled: !!summaryInput,
  });

  const availableServices = useMemo(
    () => (summary?.services || []).filter((service) => service.channelEnabled),
    [summary?.services],
  );

  useEffect(() => {
    const normalized = normalizeInitialContractServiceIds(
      value.serviceIds,
      availableServices.map((service) => service.id),
    );

    if (normalized.length !== value.serviceIds.length) {
      onChange({
        ...value,
        serviceIds: normalized,
      });
    }
  }, [availableServices, onChange, value]);

  const handleFieldChange = (field: keyof InitialClientContractDraft, nextValue: string) => {
    onChange({
      ...value,
      [field]: nextValue,
    });
  };

  const handleToggleService = (serviceId: number, checked: boolean) => {
    onChange({
      ...value,
      serviceIds: toggleInitialContractService(value.serviceIds, serviceId, checked),
    });
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900">Contrato inicial do cliente</h3>
          <p className="max-w-3xl text-sm leading-6 text-slate-600">
            O cadastro inicial do cliente exige a formalização contratual já nesta etapa. Primeiro preencha a Visão Geral e, em seguida,
            defina o contrato que habilitará os serviços comercialmente permitidos para o canal selecionado.
          </p>
        </div>
      </section>

      {!summaryInput && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Selecione um canal válido na Visão Geral para carregar o catálogo de serviços elegíveis ao contrato inicial.
          </AlertDescription>
        </Alert>
      )}

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Catálogo do canal</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{summary?.metrics?.servicesInCatalog ?? 0}</p>
          <p className="mt-2 text-sm text-slate-600">Serviços comercialmente habilitados para este novo cliente contratar.</p>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Serviços selecionados</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{value.serviceIds.length}</p>
          <p className="mt-2 text-sm text-slate-600">Serviços que farão parte do contrato inicial no momento do cadastro.</p>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Status inicial</p>
          <p className="mt-2 text-3xl font-semibold capitalize text-slate-900">{value.status}</p>
          <p className="mt-2 text-sm text-slate-600">O contrato será persistido com este status logo após a criação da empresa.</p>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2 lg:col-span-2">
            <Label htmlFor="initialContractChannel">Canal responsável do contrato</Label>
            <Input
              id="initialContractChannel"
              value={channelId ? `Canal #${channelId}` : "Selecione um canal na Visão Geral"}
              disabled
            />
            <p className="text-xs leading-5 text-slate-500">
              No cadastro inicial do cliente, o contrato já nasce vinculado explicitamente ao canal selecionado na Visão Geral. Após a criação, a eventual troca desse vínculo seguirá a governança própria do contrato.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="initialContractName">Nome do contrato *</Label>
            <Input
              id="initialContractName"
              value={value.contractName}
              onChange={(event) => handleFieldChange("contractName", event.target.value)}
              placeholder="Ex.: Contrato Portal PJC 2026"
              disabled={readOnly}
              className={fieldErrors.contractName ? "border-red-500 focus-visible:ring-red-500" : undefined}
            />
            {fieldErrors.contractName && <p className="text-sm text-red-600">{fieldErrors.contractName}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="initialContractNumber">Número do contrato *</Label>
            <Input
              id="initialContractNumber"
              value={value.contractNumber}
              onChange={(event) => handleFieldChange("contractNumber", event.target.value)}
              placeholder="Ex.: PJC-2026-001"
              disabled={readOnly}
              className={fieldErrors.contractNumber ? "border-red-500 focus-visible:ring-red-500" : undefined}
            />
            {fieldErrors.contractNumber && <p className="text-sm text-red-600">{fieldErrors.contractNumber}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="initialContractStatus">Status *</Label>
            <select
              id="initialContractStatus"
              value={value.status}
              onChange={(event) => handleFieldChange("status", event.target.value)}
              className={`flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm ${fieldErrors.status ? "border-red-500 focus-visible:ring-red-500" : "border-slate-200"}`}
              disabled={readOnly}
            >
              <option value="draft">Rascunho</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="expired">Expirado</option>
              <option value="cancelled">Cancelado</option>
            </select>
            {fieldErrors.status && <p className="text-sm text-red-600">{fieldErrors.status}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="initialContractSignedAt">Assinado em *</Label>
            <Input
              id="initialContractSignedAt"
              type="date"
              value={value.signedAt}
              onChange={(event) => handleFieldChange("signedAt", event.target.value)}
              disabled={readOnly}
              className={fieldErrors.signedAt ? "border-red-500 focus-visible:ring-red-500" : undefined}
            />
            {fieldErrors.signedAt && <p className="text-sm text-red-600">{fieldErrors.signedAt}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="initialContractStartsAt">Início da vigência *</Label>
            <Input
              id="initialContractStartsAt"
              type="date"
              value={value.startsAt}
              onChange={(event) => handleFieldChange("startsAt", event.target.value)}
              disabled={readOnly}
              className={fieldErrors.startsAt ? "border-red-500 focus-visible:ring-red-500" : undefined}
            />
            {fieldErrors.startsAt && <p className="text-sm text-red-600">{fieldErrors.startsAt}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="initialContractEndsAt">Fim da vigência *</Label>
            <Input
              id="initialContractEndsAt"
              type="date"
              value={value.endsAt}
              onChange={(event) => handleFieldChange("endsAt", event.target.value)}
              disabled={readOnly}
              className={fieldErrors.endsAt ? "border-red-500 focus-visible:ring-red-500" : undefined}
            />
            {fieldErrors.endsAt && <p className="text-sm text-red-600">{fieldErrors.endsAt}</p>}
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <Label htmlFor="initialContractNotes">Observações</Label>
          <textarea
            id="initialContractNotes"
            value={value.notes}
            onChange={(event) => handleFieldChange("notes", event.target.value)}
            className="min-h-24 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            placeholder="Observações comerciais ou operacionais do contrato"
            disabled={readOnly}
          />
        </div>

        <div className="mt-6 space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Serviços incluídos no contrato *</h4>
            <p className="text-sm text-slate-500">
              Apenas serviços já habilitados no canal aparecem como elegíveis para contratação desta empresa.
            </p>
            {fieldErrors.serviceIds && <p className="mt-2 text-sm text-red-600">{fieldErrors.serviceIds}</p>}
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {availableServices.map((service) => {
              const checked = value.serviceIds.includes(service.id);
              return (
                <label
                  key={service.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 text-sm transition ${checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300"
                    checked={checked}
                    onChange={(event) => handleToggleService(service.id, event.target.checked)}
                    disabled={readOnly}
                  />
                  <span className="space-y-1">
                    <span className="block font-medium text-slate-900">{service.name}</span>
                    <span className="block text-slate-500">{service.description || service.contractLabel}</span>
                  </span>
                </label>
              );
            })}
            {!isLoading && summaryInput && availableServices.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                Este canal ainda não possui serviços habilitados comercialmente para vinculação contratual.
              </div>
            )}
            {isLoading && summaryInput && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
                Carregando o catálogo comercial disponível para o canal selecionado...
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
